import {
  CohortBuilderItemChangedBy,
  CohortBuilderItemChangeStatusLegacy,
  type CohortBuilderDefinitionLegacy,
} from '@verantos-dev/types-cohorts';

import { checkStudyPeriodRestrictionsStatus } from './checkStudyPeriodRestrictionsStatus';
import { computeDiffForCohortBuilderItem } from './computeDiffForCohortBuilderItem';

type CohortBuilderItem =
  | CohortBuilderDefinitionLegacy['indexDate']
  | CohortBuilderDefinitionLegacy['subjectVariables'][number]
  | CohortBuilderDefinitionLegacy['simpleEvents'][number]
  | CohortBuilderDefinitionLegacy['derivedEvents'][number]
  | NonNullable<CohortBuilderDefinitionLegacy['analyticalVariables']>[number];

const getChangeStatus = (
  prevItem: CohortBuilderItem,
  nextItem: CohortBuilderItem,
  clearOldStatus: boolean
): CohortBuilderItemChangeStatusLegacy | undefined => {
  if (!nextItem) return undefined;
  if (!prevItem) {
    return CohortBuilderItemChangeStatusLegacy.NEW;
  } else if (computeDiffForCohortBuilderItem(prevItem, nextItem)) {
    return CohortBuilderItemChangeStatusLegacy.EDITED;
  } else {
    return clearOldStatus ? undefined : prevItem.changeStatus;
  }
};

/**
 * Calculates which index date, subject variables, simple events, and derived events have changed.
 * Returns a new CohortBuilderDefinition with the changes indicated on each.
 * Cases are CohortBuilderItemChangeStatus:
 *  NEW = 'New',
 *  EDITED = 'Edited'
 *  DELETED = 'Deleted',
 *  UNCHANGED = 'Unchanged'
 */
export const addChangeStatusToCohortBuilderItems = (
  prevCohortDefinition: CohortBuilderDefinitionLegacy | undefined,
  nextCohortDefinition: CohortBuilderDefinitionLegacy,
  clearOldStatus = false,
  changedBy: CohortBuilderItemChangedBy = CohortBuilderItemChangedBy.RESEARCH_ASSISTANT
): {
  cohortDefinitionWithStatuses: CohortBuilderDefinitionLegacy;
  updatedCohortItems: string[];
} => {
  const prevIndexDate = prevCohortDefinition?.indexDate;
  const nextIndexDate = nextCohortDefinition.indexDate;

  const prevSubjectVariables = prevCohortDefinition?.subjectVariables;
  const nextSubjectVariables = nextCohortDefinition.subjectVariables;

  const prevSimpleEvents = prevCohortDefinition?.simpleEvents;
  const nextSimpleEvents = nextCohortDefinition.simpleEvents;

  const prevDerivedEvents = prevCohortDefinition?.derivedEvents;
  const nextDerivedEvents = nextCohortDefinition.derivedEvents;

  const updatedCohortItems = [
    // ids of items that were removed
    ...(prevIndexDate && !nextIndexDate ? [prevIndexDate.id] : []),
    ...(prevSubjectVariables
      ?.filter(
        (prevSubjectVariable) =>
          !nextSubjectVariables.some(
            (nextSubjectVariable) => nextSubjectVariable.id === prevSubjectVariable.id
          )
      )
      .map((prevSubjectVariable) => prevSubjectVariable.id) || []),
    ...(prevSimpleEvents
      ?.filter(
        (prevSimpleEvent) =>
          !nextSimpleEvents.some((nextSimpleEvent) => nextSimpleEvent.id === prevSimpleEvent.id)
      )
      .map((prevSimpleEvent) => prevSimpleEvent.id) || []),
    ...(prevDerivedEvents
      ?.filter(
        (prevDerivedEvent) =>
          !nextDerivedEvents.some((nextDerivedEvent) => nextDerivedEvent.id === prevDerivedEvent.id)
      )
      .map((prevDerivedEvent) => prevDerivedEvent.id) || []),
  ];

  const getStatusAndUpdateItems = (prevItem: CohortBuilderItem, nextItem: CohortBuilderItem) => {
    const changeStatus = getChangeStatus(prevItem, nextItem, clearOldStatus);
    if (
      nextItem &&
      (changeStatus === CohortBuilderItemChangeStatusLegacy.NEW ||
        changeStatus === CohortBuilderItemChangeStatusLegacy.EDITED)
    ) {
      updatedCohortItems.push(nextItem.id);
    }
    return changeStatus;
  };

  const getChangedBy = (
    status: CohortBuilderItemChangeStatusLegacy | undefined
  ): CohortBuilderItemChangedBy | undefined =>
    status === CohortBuilderItemChangeStatusLegacy.NEW ||
    status === CohortBuilderItemChangeStatusLegacy.EDITED
      ? changedBy
      : undefined;

  const indexDateChangeStatus = nextIndexDate
    ? getStatusAndUpdateItems(prevIndexDate, nextIndexDate)
    : undefined;

  const updatedCohortDefinition: CohortBuilderDefinitionLegacy = {
    ...nextCohortDefinition,
    indexDate: nextIndexDate
      ? {
          ...nextIndexDate,
          changeStatus: indexDateChangeStatus,
          changedBy: getChangedBy(indexDateChangeStatus),
        }
      : undefined,
    subjectVariables: nextSubjectVariables.map((nextSubjectVariable) => {
      const prevSubjectVariable = prevSubjectVariables?.find(
        (prevSubjectVariable) => prevSubjectVariable.name === nextSubjectVariable.name
      );
      const status = getStatusAndUpdateItems(prevSubjectVariable, nextSubjectVariable);
      return {
        ...nextSubjectVariable,
        changeStatus: status,
        changedBy: getChangedBy(status),
      };
    }),
    simpleEvents: nextSimpleEvents.map((nextSimpleEvent) => {
      const prevSimpleEvent = prevSimpleEvents?.find(
        (prevSimpleEvent) => prevSimpleEvent.name === nextSimpleEvent.name
      );
      const status = getStatusAndUpdateItems(prevSimpleEvent, nextSimpleEvent);
      return {
        ...nextSimpleEvent,
        changeStatus: status,
        changedBy: getChangedBy(status),
      };
    }),
    derivedEvents: nextDerivedEvents.map((nextDerivedEvent) => {
      const prevDerivedEvent = prevDerivedEvents?.find(
        (prevDerivedEvent) => prevDerivedEvent.name === nextDerivedEvent.name
      );
      const status = getStatusAndUpdateItems(prevDerivedEvent, nextDerivedEvent);
      return {
        ...nextDerivedEvent,
        changeStatus: status,
        changedBy: getChangedBy(status),
      };
    }),
    studyPeriodRestrictions: nextCohortDefinition?.studyPeriodRestrictions
      ? (() => {
          const spStatus = checkStudyPeriodRestrictionsStatus(
            prevCohortDefinition?.studyPeriodRestrictions,
            nextCohortDefinition?.studyPeriodRestrictions
          );
          return {
            overallStudyPeriodRestriction:
              nextCohortDefinition?.studyPeriodRestrictions?.overallStudyPeriodRestriction,
            baselinePeriodRestriction:
              nextCohortDefinition?.studyPeriodRestrictions?.baselinePeriodRestriction,
            followUpPeriodRestriction:
              nextCohortDefinition?.studyPeriodRestrictions?.followUpPeriodRestriction,
            changeStatus: spStatus,
            changedBy: getChangedBy(spStatus),
          };
        })()
      : undefined,
    analyticalVariables: (nextCohortDefinition.analyticalVariables ?? []).map((nextAv) => {
      const prevAv = prevCohortDefinition?.analyticalVariables?.find(
        (prev) => prev.id === nextAv.id
      );
      const status = getStatusAndUpdateItems(
        prevAv as CohortBuilderItem,
        nextAv as CohortBuilderItem
      );
      return {
        ...nextAv,
        changeStatus: status,
        changedBy: getChangedBy(status),
      };
    }),
    criteria: nextCohortDefinition.criteria,
  };

  return {
    cohortDefinitionWithStatuses: updatedCohortDefinition,
    updatedCohortItems,
  };
};
